/*
  Flash Deals (<lp-flash-deals>): el comportamiento del diseño "Flash 2" del artifact Bundle Store PDP.
  Lo carga sections/lp-flash-deals.liquid; los estilos, en el stylesheet de esa misma section.
  Cada instancia se arma sola al conectarse (también cuando el editor del theme vuelve a renderizar la section).
  · Reloj: cuenta regresiva a data-end. El grande salta con la receta number-pop-in; el compacto cambia quieto.
    Las centésimas corren cada 47ms y bajan de 99 a 00 justo cuando cambia el segundo. Con más de 99 horas muestra
    días (41d 02:45:58) en vez de horas de tres cifras (si las horas sobrantes son 0 no se escriben: 41d 45:58); con
    data-show-days (setting "Mostrar días"), desde las 24 horas;
    con data-days-only (setting "Solo días"), desde las 24 horas y solo los días (3d).
  · Texto que entra (receta texts-reveal) y entrada de cada imagen (estilo Polymer), en cascada, la primera vez que se ven.
    Hasta que la imagen carga y termina su entrada, la card lleva .is-wait y no responde al hover.
    Las dos copias de la entrada (brillo y silueta) se crean acá y se borran al terminar.
  · Unidades: arrancan en el total y bajan hasta las que quedan cuando la card aparece en pantalla. Las que quedan llevan
    un poco de azar (data-jitter, setting "Unidades: aleatoriedad"): hasta ese % menos que las reales, fijo durante la sesión.
  · Foto: un toggle cambia todas las cards de la fila (en ola o a la vez). Las fotos no se descargan con la página: se
    piden por fila cuando el mouse entra a la fila, cuando el toggle recibe foco o, en pantallas táctiles, cuando la fila
    queda 1.2s a la vista.
  · Pausa: si la section no se ve o la pestaña está oculta, se frenan el reloj y todas las animaciones (.is-paused).
  · Carrusel como apple.com/shop: sin arrastre. Con mouse, flechas (de a una card) y scroll del trackpad; en pantallas
    táctiles no hay flechas, solo el scroll nativo del dedo.
  · Buy: agrega al carrito con el carrito de Horizon (CartAddEvent) o lleva al producto.
  En el editor del theme y con "reducir movimiento" no hay entradas: todo está a la vista.
*/
import { CartAddEvent } from '@theme/events';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const touch = window.matchMedia('(hover: none)');
const mouse = window.matchMedia('(hover: hover) and (pointer: fine)');
const $$ = (sel, root) => [...root.querySelectorAll(sel)];
const pad = n => String(n).padStart(2, '0');
const wait = ms => new Promise(r => setTimeout(r, ms));
const loaded = i => (i.complete ? Promise.resolve() : new Promise(r => {
  i.addEventListener('load', r, { once: true });
  i.addEventListener('error', r, { once: true });
}));
/* la decodificación espera como mucho 250ms: hay navegadores que no la resuelven mientras no pintan */
const decoded = i => loaded(i).then(() => i.decode && Promise.race([i.decode().catch(() => {}), wait(250)]));
const CHECK = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.4 6.6 11.4 12.5 5"/></svg>';
const LEFT_DUR = 2400;

class LpFlashDeals extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._timers = [];
    this._observers = [];
    this.editor = !!(window.Shopify && window.Shopify.designMode);
    this.calm = reduce.matches || this.editor;
    this.low = Number(this.dataset.low) || 40;
    this.total = Number(this.dataset.total) || 200;
    this.initClock();
    this.initPause();
    this.initReveal();
    this.initRows();
    this.initImages();
    this.initUnits();
    this.initPhotos();
    this.initHoverImages();
    this.initBuy();
  }

  disconnectedCallback() {
    this._timers.forEach(t => { clearInterval(t); clearTimeout(t); });
    clearInterval(this._clockT);
    this._observers.forEach(o => o.disconnect());
    document.removeEventListener('visibilitychange', this._onVis);
    mouse.removeEventListener('change', this._onMouse);
    this._ready = false;
  }

  observe(cb, opts) {
    const io = new IntersectionObserver(cb, typeof opts === 'number' ? { threshold: opts } : opts);
    this._observers.push(io);
    return io;
  }

  /* ───────── Reloj ───────── */
  initClock() {
    const clocks = $$('[data-clock]', this);
    if (!clocks.length) return;
    const end = new Date(clocks[0].dataset.end).getTime();
    if (Number.isNaN(end)) { clocks.forEach(c => { c.hidden = true; }); return; }
    const runMs = !reduce.matches;
    let shown = '';
    const daysOnly = this.hasAttribute('data-days-only');
    const dayFrom = daysOnly || this.hasAttribute('data-show-days') ? 24 : 100;
    this._tick = () => {
      const ms = Math.max(0, end - Date.now());
      const r = Math.floor(ms / 1000);
      const h = Math.floor(r / 3600);
      const m = Math.floor(r / 60) % 60;
      /* con más de 99 horas (o desde 24 con data-show-days) pasa a días (41d 02:45:58); la raíz lleva data-days para el CSS */
      const d = h >= dayFrom ? Math.floor(h / 24) : 0;
      /* con días y 0 horas sobrantes no se escribe el 00: 3d 33:44 en vez de 3d 00:33:44 */
      const str = d && daysOnly ? `${d}d` : `${d ? `${d}d${h % 24 ? `${pad(h % 24)}:` : ''}` : `${pad(h)}:`}${pad(m)}:${pad(r % 60)}`;
      if (str !== shown) {
        this.toggleAttribute('data-days', d > 0);
        const label = !shown || r % 60 === 0 ? `Ends in ${d ? `${d} ${d === 1 ? 'day' : 'days'} ${h % 24} h` : `${h} h`} ${m} min` : '';
        clocks.forEach(c => this.setDigits(c, str, shown, label));
        shown = str;
      }
      if (runMs) {
        const c = pad(Math.floor((ms % 1000) / 10));
        clocks.forEach(clock => $$('.fb-ms .t-digit', clock).forEach((d, i) => { d.textContent = c[i]; }));
      }
      if (ms === 0) {
        this._ended = true;
        clearInterval(this._clockT);
        if (this.hasAttribute('data-hide-when-ended') && !this.editor) this.hidden = true;
      }
    };
    this._tickEvery = runMs ? 47 : 1000;
    this.runClock(true);
  }

  runClock(on) {
    if (!this._tick) return;
    clearInterval(this._clockT);
    this._clockT = null;
    if (!on || this._ended) return;
    this._tick();
    if (!this._ended) this._clockT = setInterval(this._tick, this._tickEvery);
  }

  /* number-pop-in: solo saltan los dígitos que cambian (los de la derecha, con un poco de retraso cada uno) */
  setDigits(clock, str, prev, label) {
    const group = clock.querySelector('.t-digit-group');
    const live = !!prev && clock.hasAttribute('data-anim') && !reduce.matches;
    let same = 0;
    while (same < prev.length && same < str.length && prev[same] === str[same]) same++;
    group.classList.remove('is-animating');
    group.replaceChildren(...[...str].map((ch, i) => {
      const el = document.createElement('span');
      el.className = 't-digit' + (ch === ':' ? ' is-sep' : ch === 'd' ? ' is-unit' : '');
      el.textContent = ch;
      if (i < same) el.dataset.same = '';
      if (i === str.length - 2) el.dataset.stagger = '1';
      else if (i === str.length - 1) el.dataset.stagger = '2';
      return el;
    }));
    if (label) clock.setAttribute('aria-label', label);
    clock.removeAttribute('data-pending');
    if (!live) return;
    void group.offsetHeight;
    group.classList.add('is-animating');
  }

  /* ───────── Pausa: fuera de pantalla o con la pestaña oculta no corre nada ───────── */
  initPause() {
    let onScreen = true;
    const apply = () => {
      const paused = !onScreen || document.hidden;
      if (paused === this._paused) return;
      this._paused = paused;
      this.classList.toggle('is-paused', paused);
      this.runClock(!paused);
    };
    this._onVis = apply;
    document.addEventListener('visibilitychange', apply);
    this.observe(entries => { onScreen = entries[entries.length - 1].isIntersecting; apply(); }, 0).observe(this);
  }

  /* ───────── Texto que entra ───────── */
  initReveal() {
    if (this.calm) return;
    const items = $$('[data-reveal]', this);
    if (!items.length) return;
    this.splitTitle();
    /* Partido en palabras, el título de 2 líneas cambia de alto y la portada centrada se corre: el nav
       (u1-navbar) vuelve a medir el aire de arriba ahora, con todo todavía escondido, y no recién en el
       load, cuando ya se ve (ahí saltaba) */
    document.dispatchEvent(new CustomEvent('u1:nav:remeasure'));
    this.classList.add('fx');
    let seen = false;
    const io = this.observe(entries => {
      seen = true;
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-shown'); io.unobserve(e.target); } });
    }, 0.15);
    items.forEach(el => io.observe(el));
    this._timers.push(setTimeout(() => { if (!seen) items.forEach(el => el.classList.add('is-shown')); }, 1500));
  }

  /* El título llega como un solo texto (así el traductor traduce la frase entera, no palabra por palabra) y acá,
     ya traducido, se parte en palabras para que entren de a una */
  splitTitle() {
    const one = this.querySelector('.fb-t > .rv:only-child');
    if (!one) return;
    const words = one.textContent.trim().split(/\s+/);
    if (words.length < 2) return;
    const base = parseFloat(one.style.getPropertyValue('--i')) || 2;
    const h = one.parentNode;
    h.replaceChildren(...words.flatMap((w, i) => {
      const sp = document.createElement('span');
      sp.className = 'rv';
      sp.style.setProperty('--i', base + i * 2);
      sp.textContent = w;
      return i ? [document.createTextNode(' '), sp] : [sp];
    }));
  }

  /* ───────── Carrusel (como apple.com/shop) ─────────
     Sin arrastre con el mouse: un click siempre es un click (Buy, toggle, imagen) y nunca se mezcla con un desplazamiento.
     Con mouse se mueve con las flechas, de a una card, o con el trackpad; en cada extremo la flecha se deshabilita (y el
     CSS la apaga). En pantallas táctiles no hay flechas (las oculta el CSS): el dedo usa el scroll nativo, que solo
     cancela el toque si hubo desplazamiento */
  initRows() {
    const syncs = $$('.fd-rowwrap', this).map(wrap => {
      const row = wrap.querySelector('.fd-row');
      const prev = wrap.querySelector('[data-nav="prev"]');
      const next = wrap.querySelector('[data-nav="next"]');
      const bar = wrap.querySelector('.fd-bar');
      const sync = () => {
        /* barra fina (pantallas táctiles): el tramo oscuro es la parte visible del carrusel y sigue al dedo */
        if (bar) {
          const total = row.scrollWidth || 1;
          bar.style.setProperty('--fd-pw', Math.min(1, row.clientWidth / total).toFixed(4));
          bar.style.setProperty('--fd-px', (row.scrollLeft / total).toFixed(4));
          bar.toggleAttribute('data-full', row.clientWidth >= total - 1);
        }
        if (!mouse.matches) return;
        const max = row.scrollWidth - row.clientWidth;
        prev.disabled = row.scrollLeft <= 2;
        next.disabled = row.scrollLeft >= max - 2;
      };
      [prev, next].forEach(b => b.addEventListener('click', () => {
        const card = row.querySelector('.fd-card');
        const step = card ? card.offsetWidth + (parseFloat(getComputedStyle(row).columnGap) || 0) : 400;
        row.scrollBy({ left: b === next ? step : -step, behavior: reduce.matches ? 'auto' : 'smooth' });
      }));
      row.addEventListener('scroll', sync, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(row);
      sync();
      return sync;
    });
    /* una tablet a la que se le conecta un mouse (o al revés) */
    this._onMouse = () => syncs.forEach(s => s());
    mouse.addEventListener('change', this._onMouse);
  }

  /* ───────── Entrada de las imágenes ───────── */
  initImages() {
    const rows = $$('.fd-row', this);
    const cards = $$('.fd-card', this);
    const cardReady = c => {
      if (!c._ready) {
        const imgs = $$('.fd-img > img', c);
        imgs.forEach(i => { i.loading = 'eager'; });
        c._ready = Promise.all(imgs.map(decoded)).then(() => c.classList.add('is-ready'));
      }
      return c._ready;
    };
    rows.forEach(r => r.classList.add('ld'));
    cards.forEach(cardReady);
    /* is-wait: sin efecto hover (lo corta el CSS) mientras la imagen no cargó y no terminó su entrada. Sin animación
       (editor, reducir movimiento) alcanza con que la imagen esté lista */
    cards.forEach(c => c.classList.add('is-wait'));
    if (this.calm) {
      cards.forEach(c => cardReady(c).then(() => c.classList.remove('is-wait')));
      return;
    }
    rows.forEach(r => r.classList.add('fx-in'));
    /* las dos copias del render que hacen la entrada: el brillo (quemado, sumado a la luz) y la silueta del color del fondo */
    const addCopies = c => {
      const img = c.querySelector('.fd-img > img');
      if (!img) return;
      ['fd-glow', 'fd-white'].forEach(cls => {
        const layer = document.createElement('div');
        layer.className = cls;
        const copy = img.cloneNode();
        copy.loading = 'eager';
        layer.appendChild(copy);
        img.parentNode.appendChild(layer);
      });
    };
    const enter = batch => Promise.all(batch.map(cardReady)).then(() => batch.forEach((c, n) => {
      if (!c.isConnected || c.classList.contains('is-in')) return;
      addCopies(c);
      c.style.setProperty('--ei', n);
      c.classList.add('is-in');
      /* por si el navegador nunca avisa el fin de la animación; si la section está en pausa (oculta) las animaciones también, así que espera */
      const release = () => this._timers.push(setTimeout(() => {
        if (this.classList.contains('is-paused')) release(); else c.classList.remove('is-wait');
      }, 3500));
      release();
    }));
    let seen = false;
    const io = this.observe(entries => {
      seen = true;
      const batch = entries.filter(e => e.isIntersecting).map(e => e.target);
      batch.forEach(c => io.unobserve(c));
      if (batch.length) enter(batch);
    }, 0.25);
    cards.forEach(c => io.observe(c));
    this._timers.push(setTimeout(() => { if (!seen) enter(cards); }, 1500));
    /* terminada la entrada, las copias se van del DOM: queda la imagen real sola */
    this.addEventListener('animationend', e => {
      if (e.animationName !== 'lp-fd-fx') return;
      const card = e.target.closest('.fd-card');
      if (!card) return;
      card.classList.add('is-done');
      card.classList.remove('is-wait');
      card.querySelectorAll('.fd-glow, .fd-white').forEach(l => l.remove());
    });
  }

  /* ───────── Unidades: del total a las que quedan ───────── */
  initUnits() {
    const items = $$('.fd-row .fd-left[data-left]', this);
    if (!items.length) return;
    const fit = card => {
      $$('.fd-left b', card).forEach(b => {
        const r = document.createRange();
        r.selectNodeContents(b);
        b.style.minWidth = `${r.getBoundingClientRect().width}px`;
      });
    };
    const totalOf = p => Number(p.dataset.total) || this.total;
    /* Aleatoriedad (data-jitter, en %): se muestra lo que queda menos un número al azar de hasta ese %, nunca más que lo
       real y nunca 0 si todavía queda. El azar de cada card se guarda en la sesión, así no cambia al recargar */
    const jitter = Math.max(0, Number(this.dataset.jitter) || 0);
    const shown = (p, i) => {
      const real = Number(p.dataset.left);
      const max = Math.round(real * jitter / 100);
      if (real < 2 || !max) return real;
      const key = `lp-fd-r:${this.id}:${i}`;
      let r = Math.random();
      try {
        const saved = parseFloat(sessionStorage.getItem(key));
        if (saved >= 0 && saved <= 1) r = saved; else sessionStorage.setItem(key, r.toFixed(3));
      } catch (e) { /* sin sessionStorage: queda el azar de esta carga */ }
      return Math.max(1, real - Math.round(r * max));
    };
    const target = new Map(items.map((p, i) => [p, shown(p, i)]));
    const paint = (p, v) => {
      const tot = totalOf(p);
      $$('.fd-left', p.closest('.fd-card')).forEach(q => {
        const b = q.querySelector('b');
        b.textContent = v;
        b.style.minWidth = `${String(v).length}ch`;
        q.classList.toggle('is-low', v < this.low);
        q.classList.toggle('is-out', v < 1);
        if (q.hasAttribute('aria-label')) q.setAttribute('aria-label', v < 1 ? this.dataset.lSoldOut : (v < this.low ? this.dataset.lOnly : this.dataset.lLeft).replace('{n}', v));
        q.querySelector('.fd-lvl')?.style.setProperty('--fd-lv', Math.min(1, Math.max(0, v / tot)));
      });
    };
    if (this.calm || !this.hasAttribute('data-count')) {
      items.forEach(p => paint(p, target.get(p)));
      document.fonts.ready.then(() => $$('.fd-card', this).forEach(fit));
      return;
    }
    const count = (p, delay) => {
      const to = target.get(p);
      const from = totalOf(p);
      let t0 = null;
      const step = now => {
        if (!p.isConnected) return;
        if (t0 === null) t0 = now + delay;
        const k = Math.min(1, Math.max(0, (now - t0) / LEFT_DUR));
        /* rápido al principio y frenando al final, así las últimas se ven caer de a una */
        paint(p, Math.round(from - (from - to) * (1 - Math.pow(1 - k, 4))));
        if (k < 1) requestAnimationFrame(step); else fit(p.closest('.fd-card'));
      };
      requestAnimationFrame(step);
    };
    items.forEach(p => paint(p, totalOf(p)));
    let seen = false;
    const io = this.observe(entries => {
      seen = true;
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        const siblings = $$('.fd-left[data-left]', e.target.closest('.fd-row'));
        count(e.target, siblings.indexOf(e.target) * 120);
      });
    }, 0.6);
    items.forEach(p => io.observe(p));
    this._timers.push(setTimeout(() => {
      if (!seen) items.forEach(p => { paint(p, target.get(p)); fit(p.closest('.fd-card')); });
    }, 1500));
  }

  /* ───────── Imagen de hover (luces encendidas) ─────────
     Va en data-src: se pide recién cuando el mouse entra en la fila, así en pantallas táctiles (sin hover) no se descarga.
     El fundido lo hace el CSS (.fd-img-on) */
  initHoverImages() {
    $$('.fd-row', this).filter(r => r.querySelector('.fd-img-on img[data-src]')).forEach(row => {
      row.closest('.fd-rowwrap').addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse') return;
        $$('.fd-img-on img[data-src]', row).forEach(i => {
          if (i.dataset.srcset) i.srcset = i.dataset.srcset;
          i.src = i.dataset.src;
          i.removeAttribute('data-srcset');
          i.removeAttribute('data-src');
        });
      });
    });
  }

  /* ───────── Foto (toggle) ───────── */
  initPhotos() {
    const rows = $$('.fd-row', this).filter(r => r.querySelector('.fd-card.has-photo'));
    if (!rows.length) return;
    rows.forEach(row => {
      const wrap = row.closest('.fd-rowwrap');
      wrap.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') this.loadRow(row); });
      wrap.addEventListener('focusin', e => { if (e.target.closest('.fd-tog')) this.loadRow(row); });
    });
    /* en pantallas táctiles no hay hover que avise: la fila que queda 1.2s a la vista pide sus fotos */
    if (touch.matches) {
      const io = this.observe(entries => entries.forEach(e => {
        const row = e.target;
        clearTimeout(row._seenT);
        if (e.isIntersecting) row._seenT = setTimeout(() => { this.loadRow(row); io.unobserve(row); }, 1200);
      }), 0.5);
      rows.forEach(r => io.observe(r));
    }
    this.addEventListener('transitionend', e => {
      if (e.propertyName === '--fd-wipe' && e.target.classList.contains('fd-photo')) e.target.closest('.fd-card')?.classList.remove('is-fx');
    });
    this.addEventListener('click', async e => {
      const tog = e.target.closest('.fd-tog');
      if (!tog) return;
      tog.classList.add('tip-off');
      if (e.detail) tog.blur();
      /* si la fila todavía no tenía las fotos, se espera a que se decodifiquen (como mucho 300ms): sin eso un cuadro sale negro */
      await Promise.race([this.loadRow(tog.closest('.fd-row')), wait(300)]);
      this.toggleAll(tog.closest('.fd-card'));
    });
    /* al pasar el mouse por una card vuelve a pasar el destello */
    this.addEventListener('pointerover', e => {
      const card = e.target.closest('.fd-card');
      if (!card || e.pointerType !== 'mouse' || card.contains(e.relatedTarget)) return;
      this.replayStreak(card);
    });
    this.addEventListener('pointerout', e => {
      const tog = e.target.closest('.fd-tog');
      if (tog && !tog.contains(e.relatedTarget)) tog.classList.remove('tip-off');
    });
  }

  /* las fotos de una fila: de data-src / data-srcset a la imagen real, y decodificadas */
  loadRow(row) {
    if (!row) return Promise.resolve();
    if (!row._photos) {
      row._photos = Promise.all($$('.fd-photo > img[data-src]', row).map(i => {
        if (i.dataset.srcset) i.srcset = i.dataset.srcset;
        i.src = i.dataset.src;
        i.removeAttribute('data-srcset');
        i.removeAttribute('data-src');
        return decoded(i);
      }));
    }
    return row._photos;
  }

  replayStreak(card) {
    if (!card.getAnimations) return;
    card.getAnimations({ subtree: true }).forEach(a => {
      if (a.animationName === 'lp-fd-streak') a.currentTime = a.effect.getTiming().delay;
    });
  }

  /* la foto baja con una máscara (--fd-wipe); mientras se mueve la card lleva is-fx, así la foto sigue visible al irse */
  togglePhoto(card, want) {
    if (!card || !card.classList.contains('has-photo')) return;
    if (want !== undefined && card.classList.contains('is-photo') === want) return;
    const on = card.classList.toggle('is-photo');
    this.replayStreak(card);
    if (reduce.matches) return;
    /* la imagen que entra llega con un scale rápido (de 1.05 a 1, 420ms) */
    if (card._pulse) card._pulse.cancel();
    const target = card.querySelector(on ? '.fd-photo > img' : '.fd-img');
    if (target) card._pulse = target.animate([{ transform: 'scale(1.05)' }, { transform: 'none' }], { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    card.classList.add('is-fx');
    clearTimeout(card._fxT);
    card._fxT = setTimeout(() => card.classList.remove('is-fx'), 640);
  }

  /* Toggle: cambian todas las cards de la fila, en cascada de 100ms desde la tocada (o todas a la vez). El estado lo lleva
     la fila: si se toca muchas veces seguidas, cada toque cambia el destino y las que ya llegaron no se vuelven a mover */
  toggleAll(from) {
    const row = from.closest('.fd-row');
    const cards = $$('.fd-card.has-photo', row);
    const on = (row._togOn = !row._togOn);
    const k = Math.max(0, cards.indexOf(from));
    (row._togT || []).forEach(clearTimeout);
    row._togT = cards.map((c, i) => setTimeout(() => {
      const t = c.querySelector('.fd-tog');
      if (t) {
        t.setAttribute('aria-pressed', String(on));
        t.setAttribute('aria-label', on ? this.dataset.lShowProduct : this.dataset.lShowPhoto);
      }
      this.togglePhoto(c, on);
    }, this.dataset.tsw === 'sync' ? 0 : Math.abs(i - k) * 100));
  }

  /* ───────── Buy ───────── */
  initBuy() {
    this.addEventListener('click', e => {
      const buy = e.target.closest('.fd-buy');
      if (!buy || buy.disabled) return;
      if (buy.dataset.href) { window.location.href = buy.dataset.href; return; }
      this.addToCart(buy);
    });
  }

  async addToCart(buy) {
    const card = buy.closest('.fd-card');
    const items = [{ id: Number(buy.dataset.variant), quantity: 1 }];
    if (buy.dataset.gift) items.push({ id: Number(buy.dataset.gift), quantity: 1 });
    const sections = $$('cart-items-component', document).map(el => el.dataset.sectionId).filter(Boolean);
    const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
    /* el texto va en el label del u1-button (Buy y su copia blanca en la foto), así no se pierde la pastilla */
    const labels = $$('.fd-cfg', card).map(b => b.querySelector('.u1-btn__label') || b);
    const restore = () => {
      card.classList.remove('is-added', 'is-error');
      labels.forEach(l => { l.textContent = buy.dataset.label; });
    };
    buy.disabled = true;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      this.dispatchEvent(new CartAddEvent({}, String(buy.dataset.variant), {
        source: 'lp-flash-deals',
        itemCount: items.length,
        productId: buy.dataset.product,
        sections: data.sections,
      }));
      card.classList.add('is-added');
      labels.forEach(l => { l.innerHTML = CHECK; l.append(this.dataset.lAdded); });
    } catch (err) {
      console.error(err);
      card.classList.add('is-error');
      labels.forEach(l => { l.textContent = this.dataset.lTryAgain; });
    } finally {
      buy.disabled = false;
      clearTimeout(card._t);
      card._t = setTimeout(restore, 1400);
      if (document.activeElement === buy) buy.blur();
    }
  }
}

if (!customElements.get('lp-flash-deals')) customElements.define('lp-flash-deals', LpFlashDeals);
